import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Rate from "rc-rate";
import "rc-rate/assets/index.css";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import axios from "axios";
import { baseUrl } from "./baseUrl";
import { translation } from "./Translation";
import { SubmitButtonSkeleton } from "./SkeletonLoading";

const TABS = [
  { key: "lead", label: "سرنخ CRM" },
  { key: "comment", label: "نظر بازدید" },
  { key: "list", label: "سرنخ‌های باز" },
  { key: "activity", label: "فعالیت‌ها" },
];

const ShopCrmModal = ({ shop, setShow }) => {
  const lang = localStorage.getItem("lang") || "fa";
  const [tab, setTab] = useState("lead");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [crmEnabled, setCrmEnabled] = useState(false);
  const [hub, setHub] = useState(null);

  const [title, setTitle] = useState("");
  const [source, setSource] = useState("visitor");
  const [expectedValue, setExpectedValue] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [allowDuplicate, setAllowDuplicate] = useState(false);

  const [commentText, setCommentText] = useState("");
  const [rating, setRating] = useState(0);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  const loadActivities = useCallback(async () => {
    if (!shop?.id || !crmEnabled) {
      return;
    }
    setActivitiesLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/crm/customers/${shop.id}/activities`);
      setActivities(res.data?.data?.items || []);
    } catch (e) {
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  }, [shop, crmEnabled]);

  const loadHub = useCallback(async () => {
    if (!shop?.id) return;
    setLoading(true);
    try {
      const statusRes = await axios.get(`${baseUrl}/crm/status`);
      const enabled = statusRes.data?.data?.enabled === true;
      setCrmEnabled(enabled);
      if (enabled) {
        const hubRes = await axios.get(`${baseUrl}/crm/customers/${shop.id}/hub`);
        const data = hubRes.data?.data || {};
        setHub(data);
        setTitle(data.default_title || `سرنخ: ${shop.store_name || shop.name || ""}`);
        setSource(data.default_source || "visitor");
        if (data.has_open_lead) setTab("list");
      } else {
        setTab("comment");
      }
    } catch (e) {
      setCrmEnabled(false);
      setTab("comment");
    } finally {
      setLoading(false);
    }
  }, [shop]);

  useEffect(() => {
    loadHub();
  }, [loadHub]);

  useEffect(() => {
    if (tab === "activity" && crmEnabled) {
      loadActivities();
    }
  }, [tab, crmEnabled, loadActivities]);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("عنوان سرنخ الزامی است.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${baseUrl}/crm/leads`, {
        customer_id: shop.id,
        title: title.trim(),
        source,
        expected_value: expectedValue || undefined,
        due_date: dueDate || undefined,
        notes: notes || undefined,
        allow_duplicate: allowDuplicate,
      });
      toast.success("سرنخ CRM ثبت شد");
      await loadHub();
      await loadActivities();
      setTab("list");
      setAllowDuplicate(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "خطا در ثبت سرنخ");
    } finally {
      setSubmitting(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) {
      toast.error(translation.toast_error?.[lang] || "لطفا تمام فیلدها را پر کنید");
      return;
    }
    setSubmitting(true);
    const data = new FormData();
    data.append("customer_id", shop.id);
    data.append("comment", commentText);
    if (rating) data.append("rate", rating);
    data.append("status", "success");
    try {
      await axios.post(`${baseUrl}/visit-comment`, data);
      toast.success(translation.toast_error3?.[lang] || "نظر با موفقیت ثبت شد");
      setShow(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "خطا");
    } finally {
      setSubmitting(false);
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    setSubmitting(true);
    try {
      await axios.patch(`${baseUrl}/crm/leads/${leadId}/status`, { status });
      toast.success("وضعیت به‌روزرسانی شد");
      await loadHub();
      await loadActivities();
    } catch (err) {
      toast.error(err.response?.data?.message || "خطا");
    } finally {
      setSubmitting(false);
    }
  };

  const storeLabel = shop?.store_name || shop?.name || "فروشگاه";

  return (
    <div className="comments comments-show shop-crm-modal" id="shop-crm-modal">
      <div className="head">
        {crmEnabled ? `CRM — ${storeLabel}` : translation.Store_Comments?.[lang] || "نظرات"}
      </div>

      {crmEnabled && (
        <div className="shop-crm-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`shop-crm-tab-btn ${tab === t.key ? "active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center py-4"><SubmitButtonSkeleton /></div>
      ) : (
        <>
          {(!crmEnabled || tab === "comment") && (
            <form onSubmit={submitComment} className="new-comment mt-0">
              <div className="comment-card">
                <div className="dir-ltr mb-2">
                  <Rate value={rating} onChange={setRating} count={5} style={{ fontSize: "28px" }} />
                </div>
                <textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} />
                <div className="flex-end">
                  {!submitting ? (
                    <button type="submit">{translation.Store_Register?.[lang] || "ثبت"}</button>
                  ) : (
                    <button type="button"><SubmitButtonSkeleton /></button>
                  )}
                </div>
              </div>
            </form>
          )}

          {crmEnabled && tab === "lead" && (
            <form onSubmit={submitLead} className="comment-card">
              {hub?.has_open_lead && !allowDuplicate && (
                <div className="alert alert-warning small">
                  این فروشگاه سرنخ باز دارد.
                  <button type="button" className="btn btn-link btn-sm p-0 mr-2" onClick={() => setAllowDuplicate(true)}>
                    ثبت سرنخ جدید
                  </button>
                </div>
              )}
              <label className="small d-block text-right">عنوان سرنخ *</label>
              <input className="form-control mb-2" value={title} onChange={(e) => setTitle(e.target.value)} />
              {hub?.sources && (
                <>
                  <label className="small d-block text-right">منبع</label>
                  <select className="form-control mb-2" value={source} onChange={(e) => setSource(e.target.value)}>
                    {Object.entries(hub.sources).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </>
              )}
              <label className="small d-block text-right">ارزش پیش‌بینی</label>
              <input className="form-control mb-2" value={expectedValue} onChange={(e) => setExpectedValue(e.target.value)} />
              {hub?.value_presets?.length > 0 && (
                <div className="shop-crm-preset-row">
                  {hub.value_presets.map((p) => (
                    <button
                      key={p.amount}
                      type="button"
                      className="shop-crm-chip-btn"
                      onClick={() => setExpectedValue(String(p.amount))}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              )}
              <label className="small d-block text-right">موعد پیگیری (شمسی)</label>
              <DatePicker
                value={dueDate}
                onChange={(date) => setDueDate(date ? date.format("YYYY/MM/DD") : "")}
                calendar={persian}
                locale={persian_fa}
                format="YYYY/MM/DD"
                inputClass="shop-crm-date-input"
                containerClassName="shop-crm-date-wrap"
                calendarPosition="top-center"
                placeholder="1404/01/15"
              />
              <label className="small d-block text-right mt-2">یادداشت</label>
              <textarea className="form-control mb-2" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
              <div className="shop-crm-submit-wrap">
                {!submitting ? (
                  <button
                    type="submit"
                    className="shop-crm-submit-btn"
                    disabled={hub?.has_open_lead && !allowDuplicate}
                  >
                    ثبت سرنخ CRM
                  </button>
                ) : (
                  <button type="button" className="shop-crm-submit-btn">
                    <SubmitButtonSkeleton />
                  </button>
                )}
              </div>
            </form>
          )}

          {crmEnabled && tab === "list" && (
            <div className="shop-crm-leads-list">
              {(hub?.open_leads || []).length === 0 ? (
                <p className="text-muted text-center">سرنخ بازی نیست.</p>
              ) : (
                hub.open_leads.map((lead) => (
                  <div key={lead.id} className="shop-crm-lead-card">
                    <strong>{lead.title}</strong>
                    <div className="small text-muted">{lead.status_label}</div>
                    {lead.sla_breached && <span className="shop-crm-sla-badge">نقض SLA</span>}
                    <div className="shop-crm-status-actions">
                      {["contacted", "qualified", "won", "lost"].map((st) => (
                        <button
                          key={st}
                          type="button"
                          className="shop-crm-status-btn"
                          disabled={submitting}
                          onClick={() => updateLeadStatus(lead.id, st)}
                        >
                          {hub.statuses?.[st] || st}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {crmEnabled && tab === "activity" && (
            <div className="shop-crm-activity-list">
              {activitiesLoading ? (
                <div className="text-center py-4"><SubmitButtonSkeleton /></div>
              ) : activities.length === 0 ? (
                <p className="text-muted text-center">فعالیتی ثبت نشده است.</p>
              ) : (
                activities.map((item) => (
                  <div key={item.id} className="shop-crm-activity-item">
                    <div className="shop-crm-activity-head">
                      <span className="shop-crm-activity-type">{item.type_label}</span>
                      <span className="shop-crm-activity-date">{item.occurred_at}</span>
                    </div>
                    {item.subject && (
                      <div className="shop-crm-activity-subject">{item.subject}</div>
                    )}
                    {item.body && (
                      <div className="shop-crm-activity-body">{item.body}</div>
                    )}
                    <div className="shop-crm-activity-meta">
                      {item.actor && <span>{item.actor}</span>}
                      {item.lead_title && <span>سرنخ: {item.lead_title}</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShopCrmModal;
